import { Injectable } from "@angular/core";
import { Actions, Effect, ofType } from "@ngrx/effects";
import { switchMap, mergeMap, withLatestFrom, filter, tap, take, catchError } from "rxjs/operators";
import { select, Store } from "@ngrx/store";
import { IAppState } from "@edp-store/state/app.state";
import {
    ERequestsAndOperationsActions,
    GetRequestsAndOperations,
    SetAvailableTimeSlots,
    SelectFilters,
    SetFilters,
    SetLocalSupportList,
    SetLoadedRequestsAndOperations,
    SetRequestsAndOperations,
    SetServices,
    SetLoadedServices,
    SetLoadedSlots,
    GetRequestsAndOperationsDetails,
    SetRequestsAndOperationsDetails,
    AvailableTimeSlotsAPIError,
    SetNewRequest,
    GetServices
} from "@edp-store/actions/requests-and-operations.actions";
import { of, zip } from "rxjs";
import { ServiceService } from "@edp-services/service.service";
import {
    RequestsAndOperations, RequestsAndOperationsFactory, RequestsAndOperationsFilter,
    RequestsAndOperationsFiltersFactory
} from "@edp-models/requests-and-operations";
import { EdpResponseBody } from "@edp-models/response.model";
import { getRouterState } from "@edp-store/selectors/router.selectors";
import { RequestsOperationsService } from "@edp-services/requests-operations.service";
import { Service } from "@edp-models/service.model";
import { getSelectedRequest, requestsAndOperationsGetServices } from "@edp-store/selectors/requests-and-operations.selector";
import { TranslateService } from "@ngx-translate/core";
import { ELayoutActions, ShowLoader } from "@edp-store/actions/layout.actions";
import { RegionService } from "@edp-services/region.service";
import { RegionEnum } from "@edp-enums/region.enum";

@Injectable()
export class RequestsAndOperationsEffects {

    @Effect()
    loadAvailable$ = this.actions$.pipe(
        ofType(ERequestsAndOperationsActions.GetRequestsAndOperations),
        filter((action: GetRequestsAndOperations) => !!action.payload.contract),
        switchMap((action: GetRequestsAndOperations ) => {
            if (!this.isRegionPT()) {
                return of();
            }

            if (action.payload.localSupportList) {
                this.store.dispatch(new ShowLoader(true));
            }
            return this.requestsOperationsService.getList({
                contractId: action.payload.contract.ContractId,
                types: [
                    "PI",
                    "REC",
                    "PO"
                ],
                origin: "customer_support",
                localSupportList: action.payload.localSupportList
            }, false);
        }),
        mergeMap((requestsAndOperations: EdpResponseBody) => {
            if (!this.isRegionPT()) {
                return;
            }

            return of(
                new SetRequestsAndOperations(requestsAndOperations.Result.Collection),
                new SelectFilters(),
                new SetLoadedRequestsAndOperations(true),
                new SetLocalSupportList(false),
                new ShowLoader(false)
            );
        })
    );

    @Effect()
    loadAvailableTimeSlots$ = this.actions$.pipe(
        ofType(ERequestsAndOperationsActions.GetAvailableTimeSlots),
        tap(() => {
            this.store.dispatch(new SetLoadedSlots(false));
            this.store.dispatch(new AvailableTimeSlotsAPIError(null));
        }),
        switchMap(() => this.store.pipe(select(getSelectedRequest), take(1))),
        switchMap((request: RequestsAndOperations) => {
            return this.requestsOperationsService.getAvailableTimeSlots(
                request.ObjectId,
                request.Task.type,
                "OS"
            ).pipe(
                catchError((error: Error) => of(new AvailableTimeSlotsAPIError(error)))
            );
        }),
        switchMap((response: EdpResponseBody | AvailableTimeSlotsAPIError) => {
            return of(
                response instanceof AvailableTimeSlotsAPIError ? response : new SetAvailableTimeSlots(response.Result.Collection),
                new SetLoadedSlots(true)
            );
        })
    );

    @Effect()
    selectFilters$ = this.actions$.pipe(
        ofType(ERequestsAndOperationsActions.SelectFilters),
        withLatestFrom(this.store.select(requestsAndOperationsGetServices), this.store.select(getRouterState)),
        switchMap(([, serviceData, router]: [SelectFilters, Service[], any]) => {
            const filtersList = RequestsAndOperationsFiltersFactory.generateRequestsAndOperationsFilters(serviceData);
            const queryParams = router.state.queryParams;
            if (Object.keys(queryParams).length > 0) {
                Object.keys(queryParams).forEach(param => {
                    if (["Service", "Status"].includes(param)) {
                        const queryFilter = filtersList.find(filter => filter.urlName === param);
                        queryFilter.hasOptionSelected = true;
                        queryFilter.allSelected = false;
                        this.selectFilterOption(queryParams[param], queryFilter);
                    }
                });
            }
            return of(
                new SetFilters(filtersList),
                new SetServices(serviceData));
        })
    );

    @Effect()
    loadPaymentReference$ = this.actions$.pipe(
        ofType(ERequestsAndOperationsActions.GetServices),
        tap(() => this.store.dispatch(new ShowLoader(true))),
        switchMap((action: GetServices) => this.serviceService.getGrouped({segment: "B2C", contractId: action.payload})),
        switchMap((servicesData: Service[]) => {
            if (this.regionService.getRegion() !== RegionEnum.PT) {
                return of(new ShowLoader(false));
            }

            return of(
                new SetServices(servicesData),
                new SetLoadedServices(true),
                new ShowLoader(false)
            );
        })
    );

    @Effect()
    selectRequest$ = this.actions$.pipe(
        ofType(ERequestsAndOperationsActions.GetRequestsAndOperationsDetails),
        switchMap((action: GetRequestsAndOperationsDetails) => {
            return zip(
                this.requestsOperationsService.get({
                    ContactID: action.payload.ID,
                    Target: action.payload.Target
                }),
                of(action.payload)
            );
        }),
        switchMap(([response, request]: [EdpResponseBody, RequestsAndOperations]) => {
            const requestAndOperation: RequestsAndOperations = RequestsAndOperationsFactory.create(request);
            const requestDetails: RequestsAndOperations = response.Result.Collection;

            requestAndOperation.CreationDate = requestAndOperation.CreationDate || requestAndOperation.LastInteractionDate;
            requestAndOperation.LastInteractionDate = requestAndOperation.LastInteractionDate || requestAndOperation.CreationDate;
            requestAndOperation.Channel = requestDetails.Channel ? this.translateService.instant(requestDetails.Channel) : "";
            requestAndOperation.ContactDetail = requestDetails.ContactDetail;
            requestAndOperation.Attachments = requestDetails.Attachments;
            requestAndOperation.Details = requestDetails.Details;
            requestAndOperation.Message = requestDetails.Message;

            return of(
                new SetRequestsAndOperationsDetails(requestAndOperation),
            );
        })
    );

    @Effect()
    setNewRequest$ = this.actions$.pipe(
        ofType(ERequestsAndOperationsActions.SetNewRequest),
        switchMap((action: SetNewRequest) => {

            if (!this.isRegionPT()) {
                return of();
            }

            this.store.dispatch(new ShowLoader(true));

            return this.requestsOperationsService.setNewRequest(action.payload.request).pipe(
                switchMap(response => [
                    { type: ELayoutActions.ShowLoader, payload: false },
                    { type: ERequestsAndOperationsActions.SetLocalSupportList, payload: true},
                    { type: ERequestsAndOperationsActions.GetRequestsAndOperations, payload: {
                        contract: action.payload.contract,
                        localSupportList: true
                    }},
                    { type: ERequestsAndOperationsActions.SetNewRequestStatus, payload: {
                        success: true,
                        externalCategory: action.payload.request.externalCategory
                    }}
                ]),
                catchError((error) => [
                    { type: ELayoutActions.ShowLoader, payload: false },
                    { type: ERequestsAndOperationsActions.SetNewRequestStatus, payload: {
                        success: false,
                        externalCategory: action.payload.request.externalCategory
                    }}
                ])
            );
        })
    );


    private selectFilterOption(params: string | [string], filter: RequestsAndOperationsFilter) {
        if (Array.isArray(params)) {
            params.forEach(option => {
                try {
                    filter.options.find(opt => opt.name === option).selected = true;
                } catch (error) {}
            });
        } else {
            try {
                filter.options.find(opt => opt.name === params).selected = true;
            } catch (error) {}
        }
    }

    private isRegionPT(): boolean {
        return this.regionService.getRegion() === RegionEnum.PT;
    }

    constructor(
        private actions$: Actions,
        private requestsOperationsService: RequestsOperationsService,
        private store: Store<IAppState>,
        private serviceService: ServiceService,
        private translateService: TranslateService,
        private regionService: RegionService
    ) { }

}
